
import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import CalendarIcon from './icons/CalendarIcon';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css'; 
import './Calendar.css'; 
import type { Opportunity, AvailabilityStatus, Application } from '../types';
import { format, isSameDay } from 'date-fns';
import DollarIcon from './icons/DollarIcon';
import MapPinIcon from './icons/MapPinIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ClockIcon from './icons/ClockIcon';
import { formatTime } from '../utils/formatTimestamp';
import SparklesIcon from './icons/SparklesIcon';
import { useUI } from '../contexts/UIProvider';

type ValuePiece = Date | null;
type CalendarValue = ValuePiece | [ValuePiece, ValuePiece];
type SortByType = 'date-asc' | 'date-desc' | 'pay-desc' | 'title-asc';

const MyCalendarPage: React.FC = () => {
  const { currentUser, handleUpdateProfile, refreshUsers } = useAuth();
  const { opportunities } = useOpportunities();
  const { handleNavigation } = useNavigation();
  const [value, setValue] = useState<CalendarValue>(new Date());

  const tier = currentUser?.subscriptionTier;
  const canAccessCalendar = tier?.includes('_pro');
  
  const [startDate, endDate] = useMemo(() => {
    if (Array.isArray(value) && value[0] && value[1]) {
      return [value[0], value[1]];
    }
    const singleDate = Array.isArray(value) ? value[0] : value;
    return [singleDate, singleDate];
  }, [value]);

  const isRangeSelected = !!(startDate && endDate && !isSameDay(startDate, endDate));

  const myCalendarEvents = useMemo(() => {
    if (!currentUser) return [];
    const eventsMap = new Map<string, Opportunity>();

    // 1. Add gigs the user was accepted to
    opportunities.forEach((opp: Opportunity) => {
        const myApplication = opp.applications.find(
            (app: Application) => app.applicant.handle === currentUser.handle && app.status === 'accepted'
        );
        if (myApplication) {
            eventsMap.set(opp.id, opp);
        }
    });

    // 2. Add events the user is organizing
    opportunities.forEach((opp: Opportunity) => {
        if (opp.author.handle === currentUser.handle) {
            eventsMap.set(opp.id, opp);
        }
    });

    return Array.from(eventsMap.values());
  }, [currentUser, opportunities]);

  const eventsByDate = useMemo(() => {
    const events = new Map<string, 'gig' | AvailabilityStatus>();
    
    // Process Gigs
    myCalendarEvents.forEach(gig => {
      if (gig.startDate) {
        // String substring to avoid timezone shifts
        const startStr = gig.startDate.substring(0, 10);
        const endStr = gig.endDate ? gig.endDate.substring(0, 10) : startStr;
        
        let curr = new Date(startStr + 'T00:00:00');
        const end = new Date(endStr + 'T00:00:00');

        let safety = 0;
        while (curr <= end && safety < 366) {
            const key = format(curr, 'yyyy-MM-dd');
            events.set(key, 'gig');
            curr.setDate(curr.getDate() + 1);
            safety++;
        }
      }
    });

    // Process Manual Availability (Overrides gigs if explicitly set)
    if (currentUser?.availability) {
      for (const [dateKey, status] of Object.entries(currentUser.availability)) {
        if (!events.has(dateKey) || status === 'day_off') {
            events.set(dateKey, status as AvailabilityStatus);
        }
      }
    }
    return events;
  }, [myCalendarEvents, currentUser]);
  
  // Forces re-render when data changes
  const calendarKey = useMemo(() => {
      const availString = JSON.stringify(currentUser?.availability || {});
      const gigString = myCalendarEvents.length;
      return `cal-${availString}-${gigString}`;
  }, [currentUser, myCalendarEvents]);

  if (!canAccessCalendar) {
    return (
        <div className="bg-brand-surface rounded-lg p-8 text-center">
            <CalendarIcon className="h-12 w-12 text-brand-orange mx-auto" />
            <h2 className="mt-4 text-2xl font-bold text-white">Unlock Your Calendar</h2>
            <p className="mt-2 text-brand-muted max-w-md mx-auto">
                Calendar management is a Pro feature. Track your gigs, set availability, and organize your schedule all in one place.
            </p>
            <button
                onClick={() => handleNavigation('pumpUpTheVolume')}
                className="mt-6 bg-brand-orange text-white font-bold py-3 px-6 rounded-full hover:opacity-90 transition-opacity flex items-center justify-center mx-auto"
            >
                <SparklesIcon className="h-5 w-5 mr-2" />
                Upgrade to Pro
            </button>
        </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-brand-surface rounded-lg p-6">
        <div className="flex items-center space-x-3">
          <CalendarIcon className="h-7 w-7 text-brand-orange" />
          <h2 className="text-3xl font-bold text-white">My Calendar</h2>
        </div>
        <p className="mt-2 text-brand-muted">
          Click dates to toggle availability. Colored dots indicate your schedule.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-brand-surface rounded-lg p-4">
            <Calendar
                key={calendarKey} 
                onChange={setValue}
                value={value}
                selectRange={true}
                className="react-calendar"
                // Using tileClassName to drive the ::after pseudo-element in CSS
                tileClassName={({ date, view }) => {
                    if (view === 'month') {
                        const dateKey = format(date, 'yyyy-MM-dd');
                        const eventType = eventsByDate.get(dateKey);
                        if (eventType) {
                            return `status-${eventType}`; // e.g. "status-gig", "status-available"
                        }
                    }
                    return null;
                }}
                // We do NOT use tileContent anymore to avoid layout conflicts
                tileContent={null}
            />
             <div className="flex flex-wrap items-center justify-center gap-6 mt-6 text-sm text-brand-muted border-t border-gray-700 pt-4">
                <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 bg-[#FF4D00] rounded-full"></div>
                    <span>Gig / Event</span>
                </div>
                <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 bg-[#10B981] rounded-full"></div>
                    <span>Available</span>
                </div>
                <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 bg-[#EF4444] rounded-full"></div>
                    <span>Day Off</span>
                </div>
            </div>
        </div>

        <div className="bg-brand-surface rounded-lg p-6">
            {isRangeSelected ? (
                <RangeEditPanel startDate={startDate!} endDate={endDate!} handleUpdateProfile={handleUpdateProfile} currentUser={currentUser} refreshUsers={refreshUsers} />
            ) : (
                <SingleDayPanel selectedDate={startDate!} gigs={myCalendarEvents} handleUpdateProfile={handleUpdateProfile} currentUser={currentUser} refreshUsers={refreshUsers} />
            )}
        </div>
      </div>

      <EventList gigs={myCalendarEvents} />
    </div>
  );
};

const SingleDayPanel: React.FC<{selectedDate: Date, gigs: Opportunity[], handleUpdateProfile: any, currentUser: any, refreshUsers: any}> = ({ selectedDate, gigs, handleUpdateProfile, currentUser, refreshUsers }) => {
    
    const setAvailability = async (status: AvailabilityStatus | null) => {
        if (!currentUser) return;
        const dateKey = format(selectedDate, 'yyyy-MM-dd');
        // Ensure we copy the existing availability object safely
        const newAvailability = { ...(currentUser.availability || {}) };
        
        if (status === null) {
          delete newAvailability[dateKey];
        } else {
          newAvailability[dateKey] = status;
        }

        console.log(`[CLIENT DEBUG] Setting availability for ${dateKey} to ${status}. New map:`, newAvailability);

        const updatedUser = { ...currentUser, availability: newAvailability };
        await handleUpdateProfile(updatedUser);
        await refreshUsers(); // Force sync
    };

    const selectedDateKey = format(selectedDate, 'yyyy-MM-dd');
    const selectedDateStatus = currentUser?.availability?.[selectedDateKey];
    
    const selectedDateGigs = gigs.filter(gig => {
        if (!gig.startDate) return false;
        const startStr = gig.startDate.substring(0, 10);
        const endStr = gig.endDate ? gig.endDate.substring(0, 10) : startStr;
        return selectedDateKey >= startStr && selectedDateKey <= endStr;
    });


    return (
        <div>
            <h3 className="font-bold text-lg text-white mb-2">
                {format(selectedDate, 'MMMM do, yyyy')}
            </h3>
            {selectedDateStatus ? (
                <div className={`p-3 rounded-lg mb-4 text-center border ${selectedDateStatus === 'available' ? 'bg-green-900/50 border-green-500 text-green-100' : 'bg-red-900/50 border-red-500 text-red-100'}`}>
                    Status: <span className="font-bold uppercase">{selectedDateStatus === 'available' ? 'Available' : 'Day Off'}</span>
                </div>
            ) : (
                <div className="p-3 rounded-lg mb-4 text-center border border-gray-600 bg-gray-800/50 text-gray-400">
                    Status: <span className="font-bold">Standard</span>
                </div>
            )}

            <div className="border-t border-gray-700 pt-4">
                {selectedDateGigs.length > 0 ? (
                     <div>
                        <h4 className="font-semibold text-brand-orange mb-2">Events on this day</h4>
                        <ul className="space-y-2">
                           {selectedDateGigs.map(gig => (
                                <li key={gig.id} className="text-sm text-gray-300 flex items-center justify-between bg-gray-800 p-2 rounded border border-gray-700">
                                    <div className="flex-1">
                                        <span className="font-bold block">{gig.title}</span>
                                        {gig.startTime && <span className="text-xs text-brand-muted">{formatTime(gig.startTime)}</span>}
                                    </div>
                                </li>
                           ))}
                        </ul>
                    </div>
                ) : (
                    <>
                        <h4 className="font-semibold text-brand-orange mb-2">Set Status</h4>
                        <div className="space-y-2">
                             <StatusButton 
                                label="Mark Available" 
                                onClick={() => setAvailability('available')} 
                                isActive={selectedDateStatus === 'available'}
                                className="bg-green-900/20 text-green-300 hover:bg-green-900/40 border border-green-800"
                                activeClassName="bg-green-600 text-white border-green-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]"
                             />
                              <StatusButton 
                                label="Mark Day Off" 
                                onClick={() => setAvailability('day_off')}
                                isActive={selectedDateStatus === 'day_off'}
                                className="bg-red-900/20 text-red-300 hover:bg-red-900/40 border border-red-800"
                                activeClassName="bg-red-600 text-white border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.3)]"
                             />
                             {selectedDateStatus && (
                                 <StatusButton 
                                    label="Clear Status" 
                                    onClick={() => setAvailability(null)}
                                    className="bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600"
                                />
                             )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const RangeEditPanel: React.FC<{startDate: Date, endDate: Date, handleUpdateProfile: any, currentUser: any, refreshUsers: any}> = ({ startDate, endDate, handleUpdateProfile, currentUser, refreshUsers }) => {
    const { showToast } = useUI();

    const handleSetRangeAvailability = async (status: AvailabilityStatus | null) => {
        if (!currentUser) return;
        
        const start = new Date(startDate);
        const end = new Date(endDate);
        const currentAvailability = currentUser.availability || {};
        const newAvailability = { ...currentAvailability };

        let d = new Date(start);
        let safety = 0;
        while (d <= end && safety < 365) {
            const dateKey = format(d, 'yyyy-MM-dd');
            if (status === null) {
                delete newAvailability[dateKey];
            } else {
                newAvailability[dateKey] = status;
            }
            d.setDate(d.getDate() + 1);
            safety++;
        }

        console.log(`[CLIENT DEBUG] Setting range availability. New map:`, newAvailability);

        const updatedUser = { ...currentUser, availability: newAvailability };
        await handleUpdateProfile(updatedUser);
        await refreshUsers();
        showToast(`Availability updated for selected range.`);
    };

    return (
        <div>
            <h3 className="font-bold text-lg text-white">Editing Range</h3>
            <p className="text-sm text-brand-muted mb-4">{format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}</p>
            <div className="border-t border-gray-700 pt-4">
                <h4 className="font-semibold text-brand-orange mb-2">Bulk Actions</h4>
                <div className="space-y-2">
                    <StatusButton 
                        label="Set Range as Available" 
                        onClick={() => handleSetRangeAvailability('available')} 
                        className="bg-green-900/20 text-green-300 hover:bg-green-900/40 border border-green-800"
                    />
                    <StatusButton 
                        label="Set Range as Day Off" 
                        onClick={() => handleSetRangeAvailability('day_off')}
                        className="bg-red-900/20 text-red-300 hover:bg-red-900/40 border border-red-800"
                    />
                    <StatusButton 
                        label="Clear Status for Range" 
                        onClick={() => handleSetRangeAvailability(null)}
                        className="bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600"
                    />
                </div>
            </div>
        </div>
    );
};

const EventList: React.FC<{gigs: Opportunity[]}> = ({ gigs }) => {
    const [sortBy, setSortBy] = useState<SortByType>('date-asc');
    const { handleViewOpportunity } = useNavigation();

    const sortedGigs = useMemo(() => {
        const sorted = [...gigs];
        sorted.sort((a, b) => {
            switch (sortBy) {
                case 'pay-desc':
                    return (b.salary?.amount || 0) - (a.salary?.amount || 0);
                case 'title-asc':
                    return a.title.localeCompare(b.title);
                case 'date-desc':
                    return new Date(b.startDate || 0).getTime() - new Date(a.startDate || 0).getTime();
                case 'date-asc':
                default:
                    return new Date(a.startDate || 0).getTime() - new Date(b.startDate || 0).getTime();
            }
        });
        return sorted;
    }, [gigs, sortBy]);

    if (gigs.length === 0) return null;

    return (
        <div className="bg-brand-surface rounded-lg p-6">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
                <h3 className="text-xl font-bold text-white">My Events ({gigs.length})</h3>
                <div className="relative">
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as SortByType)}
                        className="bg-gray-700 text-white rounded-md py-2 pl-3 pr-8 focus:outline-none focus:ring-2 focus:ring-brand-orange appearance-none"
                    >
                        <option value="date-asc">Date (Soonest)</option>
                        <option value="date-desc">Date (Latest)</option>
                        <option value="pay-desc">Pay (High-Low)</option>
                        <option value="title-asc">Title (A-Z)</option>
                    </select>
                    <ChevronDownIcon className="h-5 w-5 absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none"/>
                </div>
            </div>
            <div className="space-y-4">
                {sortedGigs.map(gig => <EventCard key={gig.id} gig={gig} onClick={() => handleViewOpportunity(gig)} />)}
            </div>
        </div>
    );
};

const EventCard: React.FC<{gig: Opportunity, onClick: () => void}> = ({ gig, onClick }) => {
    const { currentUser } = useAuth();
    const isOrganized = currentUser && gig.author.handle === currentUser.handle;
    
    const formattedDate = useMemo(() => {
        if (!gig.startDate) return 'Date TBD';
        const start = format(new Date(`${gig.startDate}T00:00:00`), 'MMM d, yyyy');
        if (!gig.endDate || isSameDay(new Date(`${gig.startDate}T00:00:00`), new Date(`${gig.endDate}T00:00:00`))) return start;
        const end = format(new Date(`${gig.endDate}T00:00:00`), 'MMM d, yyyy');
        return `${start} - ${end}`;
    }, [gig.startDate, gig.endDate]);

    return (
        <div onClick={onClick} className="bg-gray-800/50 p-4 rounded-lg hover:bg-gray-700/50 cursor-pointer transition-colors border border-gray-700 hover:border-gray-600">
            <div className="flex justify-between items-start flex-wrap gap-2">
                <div>
                    <h4 className="font-bold text-white group-hover:underline text-lg">{gig.title}</h4>
                    <p className="text-sm text-brand-muted">
                        {isOrganized ? 'You are organizing' : `with ${gig.author.name}`}
                    </p>
                </div>
                <div className="text-sm bg-brand-orange/20 text-brand-orange font-semibold px-3 py-1 rounded-full border border-brand-orange/30">
                    {formattedDate}
                </div>
            </div>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-gray-300">
                 <div className="flex items-center gap-1.5">
                    <MapPinIcon className="h-4 w-4 text-brand-muted" />
                    <span>{gig.location}</span>
                </div>
                {gig.startTime && (
                    <div className="flex items-center gap-1.5">
                        <ClockIcon className="h-4 w-4 text-brand-muted" />
                        <span>
                            {formatTime(gig.startTime)}
                            {gig.endTime && ` - ${formatTime(gig.endTime)}`}
                        </span>
                    </div>
                )}
                {gig.salary && (
                    <div className="flex items-center gap-1.5">
                        <DollarIcon className="h-4 w-4 text-brand-muted" />
                        <span>
                            {gig.salary.currency}{gig.salary.amount.toLocaleString()}
                            {gig.salary.period !== 'flat rate' && ` / ${gig.salary.period}`}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

interface StatusButtonProps {
    label: string;
    onClick: () => void;
    isActive?: boolean;
    className: string;
    activeClassName?: string;
}
const StatusButton: React.FC<StatusButtonProps> = ({ label, onClick, isActive, className, activeClassName }) => (
     <button 
        onClick={onClick}
        className={`w-full text-left p-3 rounded-md font-semibold text-sm transition-all duration-200 ${isActive ? activeClassName : className}`}
    >
        {label}
    </button>
)

export default MyCalendarPage;
